import { memo, useRef, useEffect, useCallback } from 'react'
import type { FC } from 'react'
import { MODALS } from '@/context'
import { motion, useInView, Variants } from 'motion/react'
import { useModal, useOverlay } from '@/hooks'
import { designGrid, getMargin15, getP30Contents, getP40Contents, getP40ContentsButtonTeamUp, mq, vw } from '@/styles'
import styled from 'styled-components'

type Props = {
  text: string
  className?: string
}

export const SectionMarkdownText: FC<Props> = memo(({ text, className }) => {
  const sectionRef = useRef<HTMLElement>(null)
  const { toggle } = useModal(MODALS.CONTACT)
  const { setIsActive } = useOverlay()
  const isInView = useInView(sectionRef, { once: true, margin: '-5px' })

  const onClickBtnTinder = useCallback(() => {
    setIsActive(true)
    setTimeout(() => {
      toggle()

      setTimeout(() => {
        setIsActive(false)
      }, 150)
    }, 2500)
  }, [setIsActive, toggle])

  // Event delegation for btn--tinder buttons
  useEffect(() => {
    const section = sectionRef.current
    if (!section) return

    const handleClick = (event: Event) => {
      const target = event.target as HTMLElement
      if (target.classList.contains('btn--tinder')) {
        event.preventDefault()
        onClickBtnTinder()
      }
    }

    section.addEventListener('click', handleClick)
    return () => section.removeEventListener('click', handleClick)
  }, [onClickBtnTinder])

  return (
    <Section ref={sectionRef} {...className && { className }} initial='initial' animate={isInView ? 'animate' : 'initial'} exit='exit' variants={sectionVariants}>
      <div dangerouslySetInnerHTML={{ __html: text }} />
    </Section>
  )
})

const Section = styled(motion.section)`
  ${designGrid({})}
  ${getP40Contents()}
  color: var(--color-text-mode);
  transition: 250ms color linear;
  padding: ${vw(10, 'mobile')} 0 ${vw(120, 'mobile')};

  ${mq.greaterThan('tablet')} {
    padding: ${vw(10, 'desktop')} 0 ${vw(220, 'desktop')};
  }

  > div {
    grid-column: 1 / span 6;

    ${mq.greaterThan('tablet')} {
      grid-column: 2 / span 9;
      padding-left: 4vw;
    }
  }

  h2, h3, h4, h5, h6 {
    text-align: left;
    ${getP40Contents()}
  }

  .br--desk-only {
    br {
      ${mq.lessThan('tablet')} {
        display: none;
      }
    }
  }

  .br--mob-only {
    br {
      ${mq.greaterThan('tablet')} {
        display: none;
      }
    }
  }

  ul {
    li {
      list-style-type: disc;
    }
  }

  dl {
    dd {
      display: list-item;
      list-style-type: square;
      margin-bottom: 5px;
      margin-left: 30px;
      padding-left: 5px;

      ${mq.greaterThan('tablet')} {
        margin-bottom: 2px;
        margin-left: 50px;
      }
    }
  }

  a {
    text-decoration: underline;
  }

  p {
    word-break: keep-all;
  }

  button {
    align-items: center;
    background-color: var(--color-background-mode);
    border: 1px solid var(--color-text-mode);
    border-radius: 100px;
    color: var(--color-text-mode);
    display: flex;
    font-size: inherit;
    ${getP40ContentsButtonTeamUp()}
    font-weight: 500;
    justify-content: center;
    margin: 0 auto;
    padding: 9px 30px;
    transition: 250ms all ease-out;

    &:hover {
      background-color: var(--color-text-mode);
      color: var(--color-background-mode);
    }

    span {
      display: flex;
      align-items: flex-start;
      align-self: flex-start;
      height: 100%;
      margin-left: 0;
      margin-right: 8px;
      transform: translateY(-1px);
    }
  }

  p {
    ul,
    dl,
    ol {
      margin: 0;
    }

    > button {
      margin: 60px 0 30px;
    }
  }

  h2, h4, h5, h6, p, a
  ul, ol, dl, dt, li, dd {
    ${getP40Contents()}
    font-weight: 500;

    b {
      font-weight: 600;
    }
  }

  h2, h3, h4, h5, h6,
  ul, ol, dl, dt, p {
    ${getMargin15()}
  }

  p > i,
  h2, h3, h4 {
    ${getP30Contents()}
    font-weight: 400;
    font-style: italic;
    /* margin-top:  ${vw(48, 'mobile')}; */
    margin-top: 80px;

    ${mq.greaterThan('tablet')} {
      /* margin-top:  ${vw(60, 'desktop')}; */
    }
  }

  h3 {
    margin-top: 100px;
  }

  h2 {
    margin-bottom: 20px;
  }

  h4 {
    margin-top: 50px;
  }

  ul, ol, dl {
    margin-bottom: 50px;
  }
`

const sectionVariants:Variants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1,
      delay: .75
    }
  },
  exit: { opacity: 0, y: 20 },
}
